import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatMoney } from "@/lib/prefs";
import { CircleDollarSign, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function SellDialog({ recipe, onSold }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/sales", { recipe_id: recipe.id, quantity_sold: Number(qty) || 1 });
      setResult(data);
      onSold?.(data);
      toast.success(`Recorded ${data.quantity_sold} × ${data.recipe_name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const close = () => { setOpen(false); setResult(null); setQty(1); };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="recipe-sell-btn">
        <CircleDollarSign className="w-4 h-4" /> Sell
      </Button>
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent data-testid="sell-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {result ? "Sale recorded" : `Record sale • ${recipe.name}`}
            </DialogTitle>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Deducts ingredient inventory automatically and snapshots the cost + margin at the current pricing.</p>
              <div>
                <Label>Quantity sold</Label>
                <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="sell-qty" autoFocus />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={close}>Cancel</Button>
                <Button onClick={submit} disabled={saving} data-testid="sell-submit" className="gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CircleDollarSign className="w-4 h-4" />}
                  Record sale
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Revenue" value={formatMoney(result.revenue)} />
                <Stat label="Margin" value={formatMoney(result.margin)} highlight />
                <Stat label="Food cost" value={formatMoney(result.food_cost)} />
                <Stat label="Labor cost" value={formatMoney(result.labor_cost)} />
              </div>
              {result.inventory_shortfalls?.length > 0 && (
                <div className="rounded gold-hair border bg-primary/5 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5 gold" />
                    <div className="label-eyebrow gold text-[0.6rem]">Inventory shortfalls</div>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    {result.inventory_shortfalls.map((s, i) => (
                      <li key={i}><span className="text-foreground">{s.name}</span>: needed {s.needed.toFixed(2)} {s.unit}, on-hand {s.on_hand.toFixed(2)} {s.unit}</li>
                    ))}
                  </ul>
                </div>
              )}
              <DialogFooter>
                <Button onClick={close} data-testid="sell-done">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded border border-border p-3 ${highlight ? "bg-primary/10" : "bg-card"}`}>
      <div className="label-eyebrow text-[0.55rem]">{label}</div>
      <div className={`font-display text-xl tabular mt-1 ${highlight ? "gold" : ""}`}>{value}</div>
    </div>
  );
}
